import { LONG_DAY_MINUTES, MIN_GAP_MINUTES } from './constants';
import { gapBetween, rangesOverlap } from './time';

/**
 * Scheduling rules (SPEC §3, decided): a conflict is overlapping shifts OR a
 * gap shorter than 1 h between a person's consecutive shifts on the same day;
 * a long day is more than 10 h assigned in one day. Both SURFACE as blockers
 * and never hard-block saving.
 */

export interface ConflictShift {
  id: string;
  volunteerId: string | null;
  eventDayId: string;
  fromMin: number;
  toMin: number;
  /** cancelled/done shifts don't participate in planning conflicts */
  state?: string;
}

export interface ShiftConflict {
  kind: 'overlap' | 'short-gap';
  volunteerId: string;
  eventDayId: string;
  shiftIds: [string, string];
  /** minutes of gap for short-gap conflicts */
  gapMin?: number;
}

export interface LongDay {
  volunteerId: string;
  eventDayId: string;
  totalMin: number;
}

const ACTIVE_STATES = new Set(['assigned', 'proposed', 'done']);

export function computeConflicts(shifts: ConflictShift[]): {
  conflicts: ShiftConflict[];
  longDays: LongDay[];
} {
  const byPersonDay = new Map<string, ConflictShift[]>();
  for (const s of shifts) {
    if (!s.volunteerId) continue;
    if (s.state && !ACTIVE_STATES.has(s.state)) continue;
    const key = `${s.volunteerId}|${s.eventDayId}`;
    const list = byPersonDay.get(key);
    if (list) list.push(s);
    else byPersonDay.set(key, [s]);
  }

  const conflicts: ShiftConflict[] = [];
  const longDays: LongDay[] = [];

  for (const [key, list] of byPersonDay) {
    const [volunteerId, eventDayId] = key.split('|') as [string, string];
    const sorted = [...list].sort((a, b) => a.fromMin - b.fromMin || a.toMin - b.toMin);

    for (let i = 0; i < sorted.length; i++) {
      for (let j = i + 1; j < sorted.length; j++) {
        const a = sorted[i]!;
        const b = sorted[j]!;
        if (rangesOverlap(a, b)) {
          conflicts.push({ kind: 'overlap', volunteerId, eventDayId, shiftIds: [a.id, b.id] });
        }
      }
    }
    // Short gaps only between consecutive non-overlapping shifts
    for (let i = 0; i < sorted.length - 1; i++) {
      const a = sorted[i]!;
      const b = sorted[i + 1]!;
      if (rangesOverlap(a, b)) continue;
      const gap = gapBetween(a, b);
      if (gap > 0 && gap < MIN_GAP_MINUTES) {
        conflicts.push({
          kind: 'short-gap',
          volunteerId,
          eventDayId,
          shiftIds: [a.id, b.id],
          gapMin: gap,
        });
      }
    }

    const totalMin = sorted.reduce((acc, s) => acc + (s.toMin - s.fromMin), 0);
    if (totalMin > LONG_DAY_MINUTES) longDays.push({ volunteerId, eventDayId, totalMin });
  }

  return { conflicts, longDays };
}

export interface WindowLike {
  fromMin: number;
  toMin: number;
}

/**
 * Uncovered parts of a post's operating windows on one day, given the shifts
 * at that post (the timeline's clickable gap blocks + coverage input).
 */
export function computeGaps(windows: WindowLike[], shifts: WindowLike[]): WindowLike[] {
  const gaps: WindowLike[] = [];
  const sortedShifts = [...shifts].sort((a, b) => a.fromMin - b.fromMin);
  for (const w of windows) {
    let cursor = w.fromMin;
    for (const s of sortedShifts) {
      if (s.toMin <= cursor || s.fromMin >= w.toMin) continue;
      if (s.fromMin > cursor) gaps.push({ fromMin: cursor, toMin: s.fromMin });
      cursor = Math.max(cursor, s.toMin);
    }
    if (cursor < w.toMin) gaps.push({ fromMin: cursor, toMin: w.toMin });
  }
  // Ignore slivers under 5 min
  return gaps.filter((g) => g.toMin - g.fromMin >= 5);
}
